import React, { useState, useEffect } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import MetalRateStrip from "../components/MetalRateStrip";
import OnboardingModal from "../components/OnboardingModal";
import {
  operatorSegmentGroups,
  operatorShippingSummary,
  operatorSegments,
} from "../data/operatorSegments";
import "./OperatorPage.css";

const JMI_SEGMENTS = operatorSegments.map((seg) => ({
  key: seg.id,
  label: seg.name,
  badge: seg.group,
  image: seg.icon,
  operatorTitle: seg.name,
  tagline: seg.hallmark || "",
  specs: seg.shipMode || "",
  marketFocus: seg.corridors || "",
  feeMin: seg.feeMin || 0,
  feeMax: seg.feeMax || 0,
  colorClass: seg.colorClass || '',
  insurance: seg.insurance || '',
  typicalKgPerMonth: seg.typicalKgPerMonth || '',
}));

const OperatorPage = () => {
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalRole, setAuthModalRole] = useState("operator");

  // Segment shipping plan filter state
  const [segmentFilter, setSegmentFilter] = useState("all");

  // Interactive Calculator State
  const [dailyLots, setDailyLots] = useState(25); // 5 to 60 lots per day
  
  // Active segment for live pipeline simulator
  const [activeCalcSegment] = useState('all');

  // Calculate earnings based on selected segment(s)
  const calculateSegmentEarnings = (segmentId, lots) => {
    if (segmentId === 'all') {
      // Calculate weighted average across all segments
      const totalWeightedFee = operatorSegments.reduce((sum, seg) => {
        const avgFee = (seg.feeMin + seg.feeMax) / 2;
        return sum + avgFee;
      }, 0);
      const avgFeePerLot = totalWeightedFee / operatorSegments.length;
      return Math.round(lots * avgFeePerLot);
    } else {
      const seg = operatorSegments.find(s => s.id === segmentId);
      if (seg) {
        const avgFee = (seg.feeMin + seg.feeMax) / 2;
        return Math.round(lots * avgFee);
      }
      return Math.round(lots * 250); // default average
    }
  };

  // Active operator segment (used by the segment showcase grid + CTA)
  const [activeSegmentObj, setActiveSegmentObj] = useState(
    JMI_SEGMENTS[0] || null,
  );
  const [selectedSegment, setSelectedSegment] = useState(
    JMI_SEGMENTS[0]?.key || "",
  );

  const OPERATOR_SEGMENTS_STORAGE_KEY = "jmiOperatorSelectedSegment";

  const handleSelectSegment = (key) => {
    const seg = JMI_SEGMENTS.find((s) => s.key === key);
    if (seg) {
      setActiveSegmentObj(seg);
      setSelectedSegment(key);
      localStorage.setItem(OPERATOR_SEGMENTS_STORAGE_KEY, key);
    }
  };

  // Restore saved segment on mount
  useEffect(() => {
    const savedSeg = localStorage.getItem(OPERATOR_SEGMENTS_STORAGE_KEY);
    if (savedSeg) {
      handleSelectSegment(savedSeg);
    }
  }, []);

  const openAuth = (role = "operator") => {
    setAuthModalRole(role);
    setAuthModalOpen(true);
  };

  // Dynamic calculations for revenue slider
  // Monthly lots = dailyLots * 26 working days
  const monthlyLots = dailyLots * 26;
  const avgGramPerLot = 350; // average 350g gold/silver/jewellery per wholesale lot
  const monthlyKgs = Math.round((monthlyLots * avgGramPerLot) / 1000);

  // Calculate based on segment selection for Live Pipeline Simulator
  const monthlyGrossRevenue = calculateSegmentEarnings(activeCalcSegment, dailyLots) * 26;
  const annualGrossRevenue = monthlyGrossRevenue * 12;
  const annualRevenueLakhs = (annualGrossRevenue / 100000).toFixed(1);
  
 
  
  const operatorWorkflow = [
    {
      num: "01",
      title: "Franchise Allocation & City Territory Lock",
      desc: "Secure exclusive regional rights for designated wholesale clusters (e.g. Zaveri Bazaar, Karol Bagh, Thrissur, Surat, Jaipur, Coimbatore) with vault compliance clearance.",
    },
    {
      num: "02",
      title: "Inward Consignment Intake",
      desc: "Receive seller shipments with digital manifest matching, preliminary electronic tare evaluation, and dual-custody inward ledger logging.",
    },
    {
      num: "03",
      title: "BIS HUID Laser Assay & Purity Check",
      desc: "Scan 6-digit laser HUID stamps against the central BIS database, verifying karatage (22K, 18K, 925), stone deductions, and net metal weight tolerances.",
    },
    {
      num: "04",
      title: "Tamper-Evident Security Pouching",
      desc: "Seal inspected jewellery into high-security serialized polyurethane pouches with cryptographic QR custody tracking that cannot be re-sealed without showing void.",
    },
    {
      num: "05",
      title: "Grade IV Insured Vault Staging",
      desc: "Stage sealed consignments inside high-security Grade IV dual-custody safes covered by comprehensive multi-crore transit insurance underwritten by top insurers.",
    },
    {
      num: "06",
      title: "Armored Transit Handover",
      desc: "Coordinate insured dispatch with certified armored logistics fleets (BVC / Sequel) using biometric driver handshakes and dual digital manifest signatures.",
    },
    {
      num: "07",
      title: "Buyer Handover & Escrow Release",
      desc: "Buyer counter-receipt with digital OTP verification triggers instant bank RTGS payout to the manufacturer and automatically credits hub fee to the operator account.",
    },
    {
      num: "08",
      title: "Instant Commission Payout (T+0)",
      desc: "Your operator processing fees, assay charges, and vault handling retainers are settled same-day directly into your registered commercial account.",
    },
  ];

  const operatorCapabilities = [
    {
      icon: "🔬",
      title: "Micro-Tare Electronic Scales (±0.01g)",
      desc: "State-of-the-art calibrated electronic scales providing zero-tolerance gross and net weight confirmation down to 0.01 grams.",
    },
    {
      icon: "⚡",
      title: "Real-Time BIS HUID Database Sync",
      desc: "Direct cloud API connection verifying hallmarking center credentials, manufacturer licence codes, and certified karat standards in seconds.",
    },
    {
      icon: "🛡️",
      title: "Tamper-Evident Security Seals",
      desc: "Industrial-grade tamper-evident packaging displaying irreversible VOID patterns if opened before authorized buyer delivery.",
    },
    {
      icon: "🏦",
      title: "Grade IV Vault Infrastructure",
      desc: "Biometric dual-key custody safes with continuous high-definition video surveillance and 100% underwritten transit coverage.",
    },
    {
      icon: "🚚",
      title: "Armored Logistics Fleet Integration",
      desc: "Direct dispatch coordination with India’s premier insured bullion carriers including GPS geofencing and armored route escorts.",
    },
    {
      icon: "💰",
      title: "Volume-Linked Revenue Engine",
      desc: "Earn predictable handling fees on every single gram and consignment lot processed in your exclusive territory, yielding ₹10L - ₹50L+ annually.",
    },
    {
      icon: "📍",
      title: "Exclusive Territory Protection",
      desc: "Operate with exclusive regional jurisdiction over designated wholesale jewellery corridors with guaranteed platform transaction flow.",
    },
    {
      icon: "💻",
      title: "Dedicated JMI Cloud Hub Terminal",
      desc: "Specialized desktop web terminal designed for barcode scanning, inward tare slips, digital manifests, and automated ledger settlements without requiring any app download.",
    },
  ];

  const operatorClusters = [
    "Zaveri Bazaar (Mumbai)",
    "Varachha (Surat)",
    "Karol Bagh (Delhi)",
    "Coimbatore",
    "Jaipur",
    "Thrissur",
    "Rajkot",
    "Kolkata",
    "Hyderabad",
    "Bengaluru",
    "Ahmedabad",
    "Chennai",
  ];

  return (
    <div className="app-wrapper operator-page-view">
      <MetalRateStrip />
      <Navbar onOpenAuthModal={openAuth} />

      {/* Operator Hero */}
      <section className="operator-hero-section">
        <div className="jmi-container operator-hero-grid">
          <div className="operator-hero-copy">
            <div className="roi-badge-highlight">
              <span className="spark-icon">⚡</span>
              <span>HIGH-ROI ASSET-BACKED FRANCHISE • EARN ₹10L - ₹50L+</span>
            </div>

            <h1 className="operator-hero-title">
              Run a JMI Regional Operator Hub — Earn Up to{" "}
              <span className="gold-gradient-text">
                ₹50 Lakhs from ₹10 Lakhs
              </span>
            </h1>

            <p className="operator-hero-sub">
              Set up an authorized JMI regional assay, vault, and logistics hub
              with ~₹10 Lakhs asset-backed capital expenditure, and generate{" "}
              <strong>₹10 Lakhs to ₹50 Lakhs+</strong> in predictable annual
              processing revenue, custody fees, and transaction commissions.
            </p>

            <div className="operator-cta-group">
              <a href="#operator-application-form" className="btn btn-primary btn-lg">
                Apply for Operator Hub Franchise →
              </a>
              <a href="#roi-calculator" className="btn btn-secondary btn-lg">
                Calculate Hub Earnings
              </a>
            </div>

            <div className="operator-quick-stats">
              <div className="stat-pill highlight-gold">
                <strong>₹10L → ₹50L+</strong> Annual Income
              </div>
              <div className="stat-pill">
                <strong>100% Insured</strong> Vault Transit
              </div>
              <div className="stat-pill">
                <strong>BIS HUID</strong> Laser Assaying
              </div>
              <div className="stat-pill">
                <strong>Zero-Imitation</strong> Real Metals Only
              </div>
              <div className="stat-pill">
                <strong>T+0</strong> Daily Settlement
              </div>
            </div>
          </div>

          {/* Regional Franchise Overview Card */}
          <div className="operator-hero-dash-col">
            <div className="franchise-summary-card">
              <div className="fsc-header">
                <div className="fsc-badge">
                  <span className="fsc-badge-dot"></span>
                  <span>REGIONAL OPERATOR FRANCHISE</span>
                </div>
                <span className="fsc-city-tag">City Territory License</span>
              </div>

              <div className="fsc-hero-kpi">
                <span className="fsc-kpi-lbl">
                  Projected Annual Revenue Potential
                </span>
                <div className="fsc-kpi-val gold-gradient-text">
                  ₹10 Lakhs - ₹50 Lakhs+
                </div>
                <span className="fsc-kpi-sub">
                  From ~₹10 Lakhs initial asset-backed capital expenditure
                </span>
              </div>

              <div className="fsc-specs-grid">
                <div className="fsc-spec-item">
                  <span className="fsc-spec-lbl">Capex Payback</span>
                  <span className="fsc-spec-val">3 - 5 Months</span>
                </div>
                <div className="fsc-spec-item">
                  <span className="fsc-spec-lbl">Settlement Cycle</span>
                  <span className="fsc-spec-val gold">T+0 Bank RTGS</span>
                </div>
                <div className="fsc-spec-item">
                  <span className="fsc-spec-lbl">Territory Rights</span>
                  <span className="fsc-spec-val">1 Exclusive Per City</span>
                </div>
                <div className="fsc-spec-item">
                  <span className="fsc-spec-lbl">Custody Security</span>
                  <span className="fsc-spec-val cyan">
                    100% Insured Transit
                  </span>
                </div>
              </div>

              <div className="fsc-features-list">
                <div className="fsc-feat-row">
                  ✓ Calibrated Micro-Tare Weighing Station (±0.01g)
                </div>
                <div className="fsc-feat-row">
                  ✓ Central BIS HUID Laser Registry Verification
                </div>
                <div className="fsc-feat-row">
                  ✓ Tamper-Evident Polyurethane Security Bagging
                </div>
                <div className="fsc-feat-row">
                  ✓ Armored Fleet Coordination (BVC / Sequel Secure)
                </div>
                <div className="fsc-feat-row">
                  ✓ Strict Zero-Imitation Policy (No One-Gram Jewellery)
                </div>
              </div>

              <div className="fsc-footer-bar">
                <button
                  className="btn btn-primary w-full"
                  onClick={() => openAuth("operator")}
                >
                  Apply for City Hub License →
                </button>
                <div className="fsc-corporate-credit">
                  Licensed by <strong>7RAYS GLOBAL PRIVATE LIMITED</strong>
                  <br />
                  <span>
                    CIN: U47733GJ2026PTC171774 • Surat, Gujarat, 395010 - India.
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FINANCIAL HIGHLIGHT: EARN UP TO 50L FROM 10L INVESTMENT */}
      <section className="roi-spotlight-section" id="roi-calculator">
        <div className="jmi-container">
          <div className="roi-spotlight-card">
            <div className="roi-header-group">
              <div className="section-label">
                FINANCIAL BLUEPRINT & UNIT ECONOMICS
              </div>
              <h2 className="section-title">
                Turn a{" "}
                <span className="gold-gradient-text">
                  ₹10 Lakhs Setup into ₹50 Lakhs
                </span>{" "}
                Annual Revenue
              </h2>
              <p className="section-subtitle">
                Unlike volatile trading businesses, a JMI Regional Operator Hub
                is a tollbooth business model. You earn fixed handling fees,
                per-gram assay commissions, and vault retainers on every
                consignment passing through your city.
              </p>
            </div>

            {/* Dynamic Interactive Earnings Calculator */}
            <div className="calc-interactive-block">
              <div className="calc-left-col">
                <div className="slider-header">
                  <span className="slider-title">
                    Estimated Daily Consignments Processed
                  </span>
                  <span className="slider-display-val">
                    {dailyLots} Lots / Day
                  </span>
                </div>

                <input
                  type="range"
                  min="5"
                  max="60"
                  step="5"
                  value={dailyLots}
                  onChange={(e) => setDailyLots(Number(e.target.value))}
                  className="lots-range-slider"
                />

                <div className="slider-ticks">
                  <span>5 (Emerging City)</span>
                  <span>25 (Mid Cluster)</span>
                  <span>45 (Major Hub)</span>
                  <span>60+ (Metro e.g. Zaveri Bazaar)</span>
                </div>

                <div className="calc-breakdown-pills">
                  <div className="cb-pill">
                    <span className="cb-lbl">Monthly Volume:</span>
                    <span className="cb-val">
                      {monthlyLots.toLocaleString("en-IN")} Consignments
                    </span>
                  </div>
                  <div className="cb-pill">
                    <span className="cb-lbl">Bullion Handled:</span>
                    <span className="cb-val">~{monthlyKgs} kg / Month</span>
                  </div>
                  <div className="cb-pill">
                    <span className="cb-lbl">Settlement Cycle:</span>
                    <span className="cb-val gold">T+0 Same-Day Bank RTGS</span>
                  </div>
                </div>
              </div>

              <div className="calc-right-col">
                <div className="earnings-highlight-box">
                  <span className="box-tag">
                    PROJECTED ANNUAL OPERATOR EARNINGS
                  </span>
                  <div className="big-earning-number">
                    ₹{annualRevenueLakhs}{" "}
                    <span className="lakhs-text">Lakhs / Year</span>
                  </div>
                  <div className="monthly-sub-stat">
                    ≈ ₹{monthlyGrossRevenue.toLocaleString("en-IN")} Monthly
                    Gross Hub Revenue
                  </div>
                  <div className="setup-payback-badge">
                    <span>
                      ⚡ Initial ₹10L Capex Payback in{" "}
                      {dailyLots >= 25 ? "3 to 5 Months" : "6 to 9 Months"}
                    </span>
                  </div>
                  <button
                    className="btn btn-primary w-full mt-3"
                    onClick={() => openAuth("operator")}
                  >
                    Reserve My City Hub Franchise →
                  </button>
                </div>
              </div>
            </div>

            {/* 3-Tier Comparison Cards: ₹10L to ₹50L+ */}
            <div className="tier-cards-grid">
              {/* Tier 1: ₹10 Lakhs */}
              <div className="tier-card tier-base">
                <div className="tier-badge">TIER 1 • EMERGING HUB</div>
                <h3 className="tier-income">
                  ₹10 - ₹15 Lakhs <span className="tier-per">/ year</span>
                </h3>
                <div className="tier-scale">
                  8 - 12 Lots / Day • ~15-20 kg/mo
                </div>
                <ul className="tier-features">
                  <li>✓ Covers Tier-3 & Emerging Jewellery Centers</li>
                  <li>✓ Basic Vault & Micro-Tare Weighing Station</li>
                  <li>✓ BIS HUID Registry App Sync</li>
                  <li>✓ Standard Armored Carrier Link (2 Pickups/day)</li>
                  <li>✓ Fixed Lot Handling & Weighing Fees</li>
                </ul>
                <div className="tier-capex">
                  Setup: ~₹8-10 Lakhs (Fully Asset-Backed)
                </div>
              </div>

              {/* Tier 2: ₹25-35 Lakhs */}
              <div className="tier-card tier-mid highlight-gold-border">
                <div className="tier-popular-tag">MOST COMMON</div>
                <div className="tier-badge gold">
                  TIER 2 • REGIONAL WHOLESALE HUB
                </div>
                <h3 className="tier-income gold-text">
                  ₹25 - ₹35 Lakhs <span className="tier-per">/ year</span>
                </h3>
                <div className="tier-scale">
                  20 - 35 Lots / Day • ~40-75 kg/mo
                </div>
                <ul className="tier-features">
                  <li>
                    ✓ Covers Major Trading Cities (Surat, Jaipur, Agra,
                    Thrissur)
                  </li>
                  <li>✓ Dual-Custody Biometric Vault & Laser XRF Assay</li>
                  <li>✓ Serialized Tamper-Evident Packaging Line</li>
                  <li>✓ Multiple Daily Armored Pickups & Drops</li>
                  <li>✓ Transit Handshake & Escrow Clearance Fees</li>
                </ul>
                <div className="tier-capex">
                  Setup: ~₹10-12 Lakhs • Payback in ~4 Months
                </div>
              </div>

              {/* Tier 3: ₹50 Lakhs+ */}
              <div className="tier-card tier-enterprise">
                <div className="tier-badge cyan">
                  TIER 3 • FLAGSHIP METRO HUB
                </div>
                <h3 className="tier-income cyan-text">
                  ₹50 Lakhs+ <span className="tier-per">/ year</span>
                </h3>
                <div className="tier-scale">
                  50 - 80+ Lots / Day • ~150-250 kg/mo
                </div>
                <ul className="tier-features">
                  <li>
                    ✓ Covers Epicenter Hubs (Zaveri Bazaar, Karol Bagh,
                    Bowbazar)
                  </li>
                  <li>✓ High-Volume Vault Grade IV Infrastructure</li>
                  <li>✓ Multi-Operator Continuous Intake Stations</li>
                  <li>✓ Dedicated Armored Logistics Escorts</li>
                  <li>✓ Maximum Basis Point Volume Share & VIP Hub Status</li>
                </ul>
                <div className="tier-capex">
                  Setup: ~₹12-15 Lakhs • Top Earning Potential
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHERE DOES THE ₹10 LAKHS SETUP GO? (TRANSPARENT CAPEX BREAKDOWN) */}
      <section className="setup-breakdown-section">
        <div className="jmi-container">
          <div className="section-header-center">
            <div className="section-label">ASSET-BACKED INVESTMENT</div>
            <h2 className="section-title">
              Where Does the{" "}
              <span className="gold-gradient-text">
                ₹10 Lakhs Setup Capital
              </span>{" "}
              Go?
            </h2>
            <p className="section-subtitle">
              Your investment is not a dead franchisee fee. It is spent 100% on
              high-value, durable security, testing, and vault hardware assets
              that remain under your physical ownership and control.
            </p>
          </div>

          <div className="capex-grid">
            <div className="capex-item-card">
              <div className="capex-icon">🏦</div>
              <div className="capex-cost">₹4.2 Lakhs</div>
              <h4>Biometric Grade-IV Security Vault</h4>
              <p>
                Heavy-duty reinforced safe with dual biometric fingerprint
                locks, time-delay mechanism, and underwritten insurance rating.
              </p>
            </div>

            <div className="capex-item-card">
              <div className="capex-icon">⚖️</div>
              <div className="capex-cost">₹2.1 Lakhs</div>
              <h4>Calibrated Micro-Tare Electronic Scales</h4>
              <p>
                NABL-certified electronic precision balance measuring down to
                ±0.01g tolerance with live digital data cable interface to JMI
                app.
              </p>
            </div>

            <div className="capex-item-card">
              <div className="capex-icon">📹</div>
              <div className="capex-cost">₹1.8 Lakhs</div>
              <h4>24/7 Multi-Angle HD Surveillance & Alarms</h4>
              <p>
                Full 360-degree IP CCTV coverage of weighing table, safe door,
                and handover counter with cloud backup and central monitoring
                link.
              </p>
            </div>

            <div className="capex-item-card">
              <div className="capex-icon">💻</div>
              <div className="capex-cost">₹1.9 Lakhs</div>
              <h4>JMI Operator Workstation & Barcode Suite</h4>
              <p>
                Dedicated touch terminal, BIS HUID laser scanner, high-speed
                thermal label printer, and encrypted tamper-evident pouch
                sealer.
              </p>
            </div>
          </div>

          <div className="capex-summary-bar">
            <div className="summary-text">
              <strong>Total Investment: ~₹10.0 Lakhs</strong> — 100% Tangible
              Security & Testing Infrastructure Owned By You.
            </div>
            <button
              className="btn btn-outline-gold"
              onClick={() => openAuth("operator")}
            >
              Request Detailed Capex Sheet →
            </button>
          </div>
        </div>
      </section>

      {/* Strict Purity Governance Banner (Zero One-Gram Jewellery Rule) */}
      <section className="strict-purity-section">
        <div className="jmi-container">
          <div className="strict-purity-banner">
            <div className="purity-badge-col">
              <span className="purity-seal">🛡️</span>
              <span className="purity-seal-text">BIS COMPLIANCE MANDATE</span>
            </div>
            <div className="purity-text-col">
              <h3 className="purity-title">
                Strict Precious Metals Standard: Zero-Tolerance for Imitation or
                One-Gram Jewellery
              </h3>
              <p className="purity-desc">
                JMI Regional Operator Hubs govern high-value B2B commercial
                trade. Our operators process
                <strong>
                  {" "}
                  exclusively authentic hallmarked precious materials
                </strong>
                : BIS 916/750 Gold, 999 Fine Silver, 925 Sterling Artefacts,
                Pt950 Platinum, and IGI/GIA Certified Natural Diamonds &
                Gemstones.
                <strong>
                  {" "}
                  One-gram jewellery, brass imitation plating, and fashion
                  imitation items are strictly prohibited{" "}
                </strong>
                from the operator custody chain to safeguard institutional
                bullion integrity.
              </p>
            </div>
            <div className="purity-check-col">
              <div className="purity-pill-item">✓ BIS HUID Mandatory</div>
              <div className="purity-pill-item">✓ XRF Assayed Purity</div>
              <div className="purity-pill-item">✓ Zero Imitation Policy</div>
            </div>
          </div>
        </div>
      </section>

      {/* 8-Step Complete Operator Custody Workflow */}
      <section className="operator-workflow-section">
        <div className="jmi-container">
          <div className="section-header-center">
            <div className="section-label">END-TO-END CUSTODY LAYER</div>
            <h2 className="section-title">
              The Regional Operator{" "}
              <span className="gold-gradient-text">Custody Workflow</span>
            </h2>
            <p className="section-subtitle">
              A disciplined 8-stage chain of custody ensuring that every gram
              dispatched across the JMI network is weight-verified,
              assay-checked, sealed, and settled with zero margin of error.
            </p>
          </div>

          <div className="operator-steps-grid">
            {operatorWorkflow.map((step, idx) => (
              <div key={idx} className="operator-workflow-card">
                <div className="op-step-num-pill">{step.num}</div>
                <h3 className="op-step-title">{step.title}</h3>
                <p className="op-step-desc">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Operator Capabilities Grid */}
      <section className="operator-capabilities-section">
        <div className="jmi-container">
          <div className="section-header-center">
            <div className="section-label">OPERATOR INFRASTRUCTURE & TOOLS</div>
            <h2 className="section-title">
              Everything You Need to Run an{" "}
              <span className="gold-gradient-text">Elite Jewellery Hub</span>
            </h2>
            <p className="section-subtitle">
              Turn your regional facility into an authorized node powering
              digital trade, certified vaulting, and high-security logistics for
              India's leading jewellery manufacturers and showrooms.
            </p>
          </div>

          <div className="operator-caps-grid">
            {operatorCapabilities.map((cap, idx) => (
              <div key={idx} className="operator-cap-item">
                <div className="cap-icon-box">{cap.icon}</div>
                <h3 className="cap-title">{cap.title}</h3>
                <p className="cap-desc">{cap.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SHIPPING SEGMENTS - All Segments with Full Details */}
      <section className="operator-shipping-segments-section">
        <div className="jmi-container">
          <div className="section-header-center">
            <div className="section-label">SHIPPING & CUSTODY NETWORK</div>
            <h2 className="section-title">
              Every Segment Fully{" "}
              <span className="gold-gradient-text">
                Insured, Sealed & Tracked
              </span>
            </h2>
            <p className="section-subtitle">
              JMI Regional Operators run a closed-loop custody network. From
              seller intake to buyer handover, every segment is armored-transit
              verified, biometrically signed, and settled same-day.
            </p>
          </div>

          {/* All 7 Shipping Segments with Full Details */}
          <div className="segment-full-grid">
            {/* Yellow Gold */}
            <div className="segment-full-card gold-segment">
              <div className="seg-full-header">
                <div className="seg-icon-wrapper" style={{ background: 'linear-gradient(135deg, #fbbf24, #d97706)' }}>
                  <span className="seg-icon">🟡</span>
                </div>
                <div className="seg-full-title-group">
                  <h3>Yellow Gold</h3>
                  <span className="seg-group-tag">Gold Segment</span>
                </div>
                <span className="seg-hallmark-badge">BIS 916/750/999</span>
              </div>
              <div className="seg-full-body">
                <div className="seg-detail-row">
                  <span className="seg-detail-label">Transit Mode</span>
                  <span className="seg-detail-value">Armored Vault-to-Vault Transit</span>
                </div>
                <div className="seg-detail-row">
                  <span className="seg-detail-label">Packaging</span>
                  <span className="seg-detail-value">Serialized tamper-evident pouch + HUID laser tag</span>
                </div>
                <div className="seg-detail-row">
                  <span className="seg-detail-label">Insurance</span>
                  <span className="seg-detail-value highlight">₹5 Crore per consignment</span>
                </div>
                <div className="seg-detail-row">
                  <span className="seg-detail-label">Custody</span>
                  <span className="seg-detail-value">Dual-custody biometric handshake</span>
                </div>
                <div className="seg-detail-row">
                  <span className="seg-detail-label">SLA</span>
                  <span className="seg-detail-value">T+1 city • T+2 inter-city</span>
                </div>
                <div className="seg-detail-row">
                  <span className="seg-detail-label">Fee</span>
                  <span className="seg-detail-value gold">₹180 – ₹320 per lot</span>
                </div>
                <div className="seg-detail-row">
                  <span className="seg-detail-label">Volume</span>
                  <span className="seg-detail-value">40 – 120 kg/month</span>
                </div>
              </div>
            </div>

            {/* White Gold */}
            <div className="segment-full-card">
              <div className="seg-full-header">
                <div className="seg-icon-wrapper" style={{ background: 'linear-gradient(135deg, #e2e8f0, #94a3b8)' }}>
                  <span className="seg-icon">⚪</span>
                </div>
                <div className="seg-full-title-group">
                  <h3>White Gold</h3>
                  <span className="seg-group-tag">Gold Segment</span>
                </div>
                <span className="seg-hallmark-badge">BIS 750/585</span>
              </div>
              <div className="seg-full-body">
                <div className="seg-detail-row">
                  <span className="seg-detail-label">Transit Mode</span>
                  <span className="seg-detail-value">Insured Bullion Courier (GPS)</span>
                </div>
                <div className="seg-detail-row">
                  <span className="seg-detail-label">Packaging</span>
                  <span className="seg-detail-value">Anti-static tamper-evident pouch</span>
                </div>
                <div className="seg-detail-row">
                  <span className="seg-detail-label">Insurance</span>
                  <span className="seg-detail-value highlight">₹3 Crore per consignment</span>
                </div>
                <div className="seg-detail-row">
                  <span className="seg-detail-label">Custody</span>
                  <span className="seg-detail-value">Dual-custody OTP verification</span>
                </div>
                <div className="seg-detail-row">
                  <span className="seg-detail-label">SLA</span>
                  <span className="seg-detail-value">T+1 within city</span>
                </div>
                <div className="seg-detail-row">
                  <span className="seg-detail-label">Fee</span>
                  <span className="seg-detail-value gold">₹150 – ₹280 per lot</span>
                </div>
                <div className="seg-detail-row">
                  <span className="seg-detail-label">Volume</span>
                  <span className="seg-detail-value">15 – 60 kg/month</span>
                </div>
              </div>
            </div>

            {/* 999 Fine Silver */}
            <div className="segment-full-card">
              <div className="seg-full-header">
                <div className="seg-icon-wrapper" style={{ background: 'linear-gradient(135deg, #cbd5e1, #64748b)' }}>
                  <span className="seg-icon">🥈</span>
                </div>
                <div className="seg-full-title-group">
                  <h3>999 Fine Silver</h3>
                  <span className="seg-group-tag">Silver Segment</span>
                </div>
                <span className="seg-hallmark-badge">BIS 999</span>
              </div>
              <div className="seg-full-body">
                <div className="seg-detail-row">
                  <span className="seg-detail-label">Transit Mode</span>
                  <span className="seg-detail-value">Bullion Armored Carrier (BVC/Sequel)</span>
                </div>
                <div className="seg-detail-row">
                  <span className="seg-detail-label">Packaging</span>
                  <span className="seg-detail-value">Heavy-gauge tamper-evident pouch</span>
                </div>
                <div className="seg-detail-row">
                  <span className="seg-detail-label">Insurance</span>
                  <span className="seg-detail-value highlight">₹2 Crore per consignment</span>
                </div>
                <div className="seg-detail-row">
                  <span className="seg-detail-label">Custody</span>
                  <span className="seg-detail-value">Dual-custody digital manifest</span>
                </div>
                <div className="seg-detail-row">
                  <span className="seg-detail-label">SLA</span>
                  <span className="seg-detail-value">T+1 city • T+3 inter-city</span>
                </div>
                <div className="seg-detail-row">
                  <span className="seg-detail-label">Fee</span>
                  <span className="seg-detail-value gold">₹90 – ₹160 per lot</span>
                </div>
                <div className="seg-detail-row">
                  <span className="seg-detail-label">Volume</span>
                  <span className="seg-detail-value">100 – 300 kg/month</span>
                </div>
              </div>
            </div>

            {/* 925 Sterling Silver */}
            <div className="segment-full-card">
              <div className="seg-full-header">
                <div className="seg-icon-wrapper" style={{ background: 'linear-gradient(135deg, #94a3b8, #475569)' }}>
                  <span className="seg-icon">🥈</span>
                </div>
                <div className="seg-full-title-group">
                  <h3>925 Sterling Silver</h3>
                  <span className="seg-group-tag">Silver Segment</span>
                </div>
                <span className="seg-hallmark-badge">BIS 925</span>
              </div>
              <div className="seg-full-body">
                <div className="seg-detail-row">
                  <span className="seg-detail-label">Transit Mode</span>
                  <span className="seg-detail-value">Insured Consolidated Freight</span>
                </div>
                <div className="seg-detail-row">
                  <span className="seg-detail-label">Packaging</span>
                  <span className="seg-detail-value">Tamper-evident batch pouch</span>
                </div>
                <div className="seg-detail-row">
                  <span className="seg-detail-label">Insurance</span>
                  <span className="seg-detail-value highlight">₹1.5 Crore per consignment</span>
                </div>
                <div className="seg-detail-row">
                  <span className="seg-detail-label">Custody</span>
                  <span className="seg-detail-value">Hub custodian countersign</span>
                </div>
                <div className="seg-detail-row">
                  <span className="seg-detail-label">SLA</span>
                  <span className="seg-detail-value">T+2 city • T+4 inter-city</span>
                </div>
                <div className="seg-detail-row">
                  <span className="seg-detail-label">Fee</span>
                  <span className="seg-detail-value gold">₹70 – ₹140 per lot</span>
                </div>
                <div className="seg-detail-row">
                  <span className="seg-detail-label">Volume</span>
                  <span className="seg-detail-value">80 – 250 kg/month</span>
                </div>
              </div>
            </div>

            {/* Platinum */}
            <div className="segment-full-card">
              <div className="seg-full-header">
                <div className="seg-icon-wrapper" style={{ background: 'linear-gradient(135deg, #38bdf8, #0369a1)' }}>
                  <span className="seg-icon">🔗</span>
                </div>
                <div className="seg-full-title-group">
                  <h3>Pt950 Platinum</h3>
                  <span className="seg-group-tag">Platinum & Stones</span>
                </div>
                <span className="seg-hallmark-badge">Pt950</span>
              </div>
              <div className="seg-full-body">
                <div className="seg-detail-row">
                  <span className="seg-detail-label">Transit Mode</span>
                  <span className="seg-detail-value">High-Security Dedicated Armored Escort</span>
                </div>
                <div className="seg-detail-row">
                  <span className="seg-detail-label">Packaging</span>
                  <span className="seg-detail-value">Reinforced serialized vault pouch</span>
                </div>
                <div className="seg-detail-row">
                  <span className="seg-detail-label">Insurance</span>
                  <span className="seg-detail-value highlight">₹8 Crore per consignment</span>
                </div>
                <div className="seg-detail-row">
                  <span className="seg-detail-label">Custody</span>
                  <span className="seg-detail-value">Triple-verification dual-custody</span>
                </div>
                <div className="seg-detail-row">
                  <span className="seg-detail-label">SLA</span>
                  <span className="seg-detail-value">T+2 scheduled pickup</span>
                </div>
                <div className="seg-detail-row">
                  <span className="seg-detail-label">Fee</span>
                  <span className="seg-detail-value gold">₹400 – ₹750 per lot</span>
                </div>
                <div className="seg-detail-row">
                  <span className="seg-detail-label">Volume</span>
                  <span className="seg-detail-value">5 – 25 kg/month</span>
                </div>
              </div>
            </div>

            {/* Diamond & Gemstone */}
            <div className="segment-full-card">
              <div className="seg-full-header">
                <div className="seg-icon-wrapper" style={{ background: 'linear-gradient(135deg, #a78bfa, #6d28d9)' }}>
                  <span className="seg-icon">💎</span>
                </div>
                <div className="seg-full-title-group">
                  <h3>Diamond & Gemstone</h3>
                  <span className="seg-group-tag">Platinum & Stones</span>
                </div>
                <span className="seg-hallmark-badge">IGI/GIA</span>
              </div>
              <div className="seg-full-body">
                <div className="seg-detail-row">
                  <span className="seg-detail-label">Transit Mode</span>
                  <span className="seg-detail-value">Registered Insured Courier</span>
                </div>
                <div className="seg-detail-row">
                  <span className="seg-detail-label">Packaging</span>
                  <span className="seg-detail-value">Gem-padded tamper-evident pouch</span>
                </div>
                <div className="seg-detail-row">
                  <span className="seg-detail-label">Insurance</span>
                  <span className="seg-detail-value highlight">₹4 Crore per consignment</span>
                </div>
                <div className="seg-detail-row">
                  <span className="seg-detail-label">Custody</span>
                  <span className="seg-detail-value">Assay-verified handover</span>
                </div>
                <div className="seg-detail-row">
                  <span className="seg-detail-label">SLA</span>
                  <span className="seg-detail-value">T+2 city • T+5 inter-city</span>
                </div>
                <div className="seg-detail-row">
                  <span className="seg-detail-label">Fee</span>
                  <span className="seg-detail-value gold">₹250 – ₹500 per lot</span>
                </div>
                <div className="seg-detail-row">
                  <span className="seg-detail-label">Volume</span>
                  <span className="seg-detail-value">2 – 15 kg/month</span>
                </div>
              </div>
            </div>

            {/* Kundan/Polki/Temple */}
            <div className="segment-full-card">
              <div className="seg-full-header">
                <div className="seg-icon-wrapper" style={{ background: 'linear-gradient(135deg, #d4af37, #a16207)' }}>
                  <span className="seg-icon">🪔</span>
                </div>
                <div className="seg-full-title-group">
                  <h3>Kundan / Polki / Temple</h3>
                  <span className="seg-group-tag">Platinum & Stones</span>
                </div>
                <span className="seg-hallmark-badge">Traditional</span>
              </div>
              <div className="seg-full-body">
                <div className="seg-detail-row">
                  <span className="seg-detail-label">Transit Mode</span>
                  <span className="seg-detail-value">Specialist Heritage Carrier</span>
                </div>
                <div className="seg-detail-row">
                  <span className="seg-detail-label">Packaging</span>
                  <span className="seg-detail-value">Climate-cushioned void-evident pouch</span>
                </div>
                <div className="seg-detail-row">
                  <span className="seg-detail-label">Insurance</span>
                  <span className="seg-detail-value highlight">₹3 Crore per consignment</span>
                </div>
                <div className="seg-detail-row">
                  <span className="seg-detail-label">Custody</span>
                  <span className="seg-detail-value">Master artisan + hub custodian</span>
                </div>
                <div className="seg-detail-row">
                  <span className="seg-detail-label">SLA</span>
                  <span className="seg-detail-value">T+3 scheduled cluster route</span>
                </div>
                <div className="seg-detail-row">
                  <span className="seg-detail-label">Fee</span>
                  <span className="seg-detail-value gold">₹300 – ₹600 per lot</span>
                </div>
                <div className="seg-detail-row">
                  <span className="seg-detail-label">Volume</span>
                  <span className="seg-detail-value">3 – 12 kg/month</span>
                </div>
              </div>
            </div>
          </div>

          {/* Active Clusters */}
          <div className="operator-segment-cities">
            <h4 className="segment-cities-title">
              Active Operator Clusters & Shipping Corridors
            </h4>
            <div className="segment-city-pills">
              {operatorClusters.map((city) => (
                <span key={city} className="segment-city-pill">
                  {city}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHO IS ELIGIBLE TO BECOME AN OPERATOR? */}
      <section className="operator-eligibility-section">
        <div className="jmi-container">
          <div className="section-header-center">
            <div className="section-label">PARTNERSHIP CRITERIA</div>
            <h2 className="section-title">
              Who Can Apply for a{" "}
              <span className="gold-gradient-text">
                JMI Operator Franchise?
              </span>
            </h2>
            <p className="section-subtitle">
              We partner with established businesses in prominent jewellery
              corridors who have the credibility, infrastructure, and local
              reputation to uphold the highest standards of physical custody.
            </p>
          </div>

          <div className="eligibility-grid">
            <div className="elig-card">
              <span className="elig-icon">🏢</span>
              <h4>Bullion Dealers & Traders</h4>
              <p>
                Existing bullion houses with commercial street access,
                established market presence, and existing security setup.
              </p>
            </div>

            <div className="elig-card">
              <span className="elig-icon">🔬</span>
              <h4>Assaying & Hallmarking Centers</h4>
              <p>
                BIS-recognized assay facilities looking to add high-volume B2B
                logistics and vault custody revenue.
              </p>
            </div>

            <div className="elig-card">
              <span className="elig-icon">💎</span>
              <h4>Senior Wholesale Jewellers</h4>
              <p>
                Wholesale manufacturers and karigar cluster leaders seeking to
                monetize regional hub logistics.
              </p>
            </div>

            <div className="elig-card">
              <span className="elig-icon">🛡️</span>
              <h4>Secured Vault & Logistics Operators</h4>
              <p>
                Certified security companies and logistics partners with
                existing Grade-IV vault and armored carrier access.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* OPERATOR SEGMENT SHIPPING & LOGISTICS PLAN */}
      <section
        className="operator-shipping-plan-section"
        id="operator-shipping-plan"
      >
        <div className="jmi-container">
          <div className="section-header-center">
            <div className="section-label">SEGMENT SHIPPING & CUSTODY PLAN</div>
            <h2 className="section-title">
              How Each{" "}
              <span className="gold-gradient-text">
                Jewellery Segment Ships
              </span>{" "}
              Through Your Hub
            </h2>
            <p className="section-subtitle">
              Every segment moves through a dedicated dual-custody lane with
              segment-specific packaging, transit insurance, and SLAs. Select a
              segment group to see its exact shipping plan, custody level, and
              per-lot handling economics for your city franchise.
            </p>
          </div>

          {/* Segment filter chips */}
          <div className="segment-filter-row">
            {operatorSegmentGroups.map((grp) => (
              <button
                key={grp.key}
                className={`segment-chip ${segmentFilter === grp.key ? "segment-chip-active" : ""}`}
                onClick={() => setSegmentFilter(grp.key)}
              >
                {grp.label}
              </button>
            ))}
          </div>

          {/* Shipping summary strip */}
          <div className="shipping-summary-strip">
            <div className="summary-pill">
              <span className="summary-label">Segments Covered</span>
              <span className="summary-value">
                {operatorShippingSummary.totalSegments}
              </span>
            </div>
            <div className="summary-pill">
              <span className="summary-label">Active Corridors</span>
              <span className="summary-value">
                {operatorShippingSummary.activeCorridors}
              </span>
            </div>
            <div className="summary-pill">
              <span className="summary-label">Max Transit Cover</span>
              <span className="summary-value">
                {operatorShippingSummary.insuredUpto}
              </span>
            </div>
            <div className="summary-pill">
              <span className="summary-label">Delivery SLA</span>
              <span className="summary-value">
                {operatorShippingSummary.avgSLA}
              </span>
            </div>
            <div className="summary-pill pill-fee">
              <span className="summary-label">Handling Fee Range</span>
              <span className="summary-value">
                {operatorShippingSummary.minFee} –{" "}
                {operatorShippingSummary.maxFee}
              </span>
            </div>
          </div>

          {/* Segment plan cards */}
          <div className="segment-plan-grid">
            <div key="yellow-gold" className="segment-plan-card">
              <div className="segment-card-head">
                <div
                  className="segment-chip-badge"
                  style={{
                    background: "rgba(212, 175, 55, 0.12)",
                    borderColor: "rgba(212, 175, 55, 0.4)",
                    color: "#facc15",
                  }}
                >
                  🟡
                </div>
                <div className="segment-card-title">
                  <h3>Yellow Gold</h3>
                  <span className="segment-group-tag">Gold</span>
                </div>
                <span className="segment-hallmark">
                  BIS 916 (22K) • 750 (18K) • 999 (24K)
                </span>
              </div>
              <div className="segment-card-body">
                <div className="plan-row">
                  <span className="plan-label">Transit Mode</span>
                  <span className="plan-value">
                    Armored Vault-to-Vault Transit
                  </span>
                </div>
                <div className="plan-row">
                  <span className="plan-label">Packaging</span>
                  <span className="plan-value">
                    Serialized tamper-evident polyurethane pouch + HUID laser
                    tag
                  </span>
                </div>
                <div className="plan-row">
                  <span className="plan-label">Transit Insurance</span>
                  <span className="plan-value">
                    ₹5 Crore transit insurance per consignment
                  </span>
                </div>
                <div className="plan-row">
                  <span className="plan-label">Custody Level</span>
                  <span className="plan-value">
                    Dual-custody biometric handshake (driver + hub custodian)
                  </span>
                </div>
                <div className="plan-row">
                  <span className="plan-label">Delivery SLA</span>
                  <span className="plan-value">
                    T+1 within city cluster • T+2 inter-city
                  </span>
                </div>
                <div className="plan-row">
                  <span className="plan-label">Handling Fee</span>
                  <span className="plan-value fee-text">
                    ₹180 – ₹320 per lot
                  </span>
                </div>
                <div className="plan-row">
                  <span className="plan-label">Typical Volume</span>
                  <span className="plan-value">40 – 120 kg</span>
                </div>
                <div className="plan-row">
                  <span className="plan-label">Key Corridors</span>
                  <span className="plan-value">
                    Zaveri Bazaar (Mumbai), Karol Bagh (Delhi), Chickpet
                    (Bangalore)
                  </span>
                </div>
              </div>
              <div className="segment-card-footer">
                <span className="coverage-tag">Coverage: Pan-India</span>
                <button
                  className="btn btn-secondary btn-sm plan-apply-btn"
                  onClick={() => openAuth("operator")}
                >
                  Apply for This Segment Lane →
                </button>
              </div>
            </div>

            <div key="white-gold" className="segment-plan-card">
              <div className="segment-card-head">
                <div
                  className="segment-chip-badge"
                  style={{
                    background: "rgba(228, 228, 231, 0.12)",
                    borderColor: "rgba(228, 228, 231, 0.4)",
                    color: "#e4e4e7",
                  }}
                >
                  ⚪
                </div>
                <div className="segment-card-title">
                  <h3>White Gold</h3>
                  <span className="segment-group-tag">Gold</span>
                </div>
                <span className="segment-hallmark">
                  BIS 750 (18K WG) • 585 (14K WG) • Rhodium flash verified
                </span>
              </div>
              <div className="segment-card-body">
                <div className="plan-row">
                  <span className="plan-label">Transit Mode</span>
                  <span className="plan-value">
                    Insured Bullion Courier (GPS Geofenced)
                  </span>
                </div>
                <div className="plan-row">
                  <span className="plan-label">Packaging</span>
                  <span className="plan-value">
                    Anti-static tamper-evident pouch + serialized QR custody
                    seal
                  </span>
                </div>
                <div className="plan-row">
                  <span className="plan-label">Transit Insurance</span>
                  <span className="plan-value">
                    ₹3 Crore transit insurance per consignment
                  </span>
                </div>
                <div className="plan-row">
                  <span className="plan-label">Custody Level</span>
                  <span className="plan-value">
                    Dual-custody OTP verification at handover
                  </span>
                </div>
                <div className="plan-row">
                  <span className="plan-label">Delivery SLA</span>
                  <span className="plan-value">T+1 within city cluster</span>
                </div>
                <div className="plan-row">
                  <span className="plan-label">Handling Fee</span>
                  <span className="plan-value fee-text">
                    ₹150 – ₹280 per lot
                  </span>
                </div>
                <div className="plan-row">
                  <span className="plan-label">Typical Volume</span>
                  <span className="plan-value">15 – 60 kg</span>
                </div>
                <div className="plan-row">
                  <span className="plan-label">Key Corridors</span>
                  <span className="plan-value">Surat, Jaipur, Coimbatore</span>
                </div>
              </div>
              <div className="segment-card-footer">
                <span className="coverage-tag">
                  Coverage: South &amp; West India corridors
                </span>
                <button
                  className="btn btn-secondary btn-sm plan-apply-btn"
                  onClick={() => openAuth("operator")}
                >
                  Apply for This Segment Lane →
                </button>
              </div>
            </div>

            <div key="fine-silver" className="segment-plan-card">
              <div className="segment-card-head">
                <div
                  className="segment-chip-badge"
                  style={{
                    background: "rgba(212, 212, 216, 0.12)",
                    borderColor: "rgba(212, 212, 216, 0.4)",
                    color: "#d4d4d8",
                  }}
                >
                  🥈
                </div>
                <div className="segment-card-title">
                  <h3>999 Fine Silver</h3>
                  <span className="segment-group-tag">Silver</span>
                </div>
                <span className="segment-hallmark">
                  BIS 999 Fine Silver hallmark stamp
                </span>
              </div>
              <div className="segment-card-body">
                <div className="plan-row">
                  <span className="plan-label">Transit Mode</span>
                  <span className="plan-value">
                    Bullion Armored Carrier (BVC / Sequel)
                  </span>
                </div>
                <div className="plan-row">
                  <span className="plan-label">Packaging</span>
                  <span className="plan-value">
                    Heavy-gauge tamper-evident pouch + barcoded tare slip
                  </span>
                </div>
                <div className="plan-row">
                  <span className="plan-label">Transit Insurance</span>
                  <span className="plan-value">
                    ₹2 Crore transit insurance per consignment
                  </span>
                </div>
                <div className="plan-row">
                  <span className="plan-label">Custody Level</span>
                  <span className="plan-value">
                    Dual-custody digital manifest signature
                  </span>
                </div>
                <div className="plan-row">
                  <span className="plan-label">Delivery SLA</span>
                  <span className="plan-value">
                    T+1 within city cluster • T+3 inter-city
                  </span>
                </div>
                <div className="plan-row">
                  <span className="plan-label">Handling Fee</span>
                  <span className="plan-value fee-text">
                    ₹90 – ₹160 per lot
                  </span>
                </div>
                <div className="plan-row">
                  <span className="plan-label">Typical Volume</span>
                  <span className="plan-value">100 – 300 kg</span>
                </div>
                <div className="plan-row">
                  <span className="plan-label">Key Corridors</span>
                  <span className="plan-value">
                    Kolkata, Siliguri, Guwahati
                  </span>
                </div>
              </div>
              <div className="segment-card-footer">
                <span className="coverage-tag">
                  Coverage: East &amp; Northeast India corridors
                </span>
                <button
                  className="btn btn-secondary btn-sm plan-apply-btn"
                  onClick={() => openAuth("operator")}
                >
                  Apply for This Segment Lane →
                </button>
              </div>
            </div>

            <div key="sterling-silver" className="segment-plan-card">
              <div className="segment-card-head">
                <div
                  className="segment-chip-badge"
                  style={{
                    background: "rgba(161, 161, 170, 0.12)",
                    borderColor: "rgba(161, 161, 170, 0.4)",
                    color: "#a1a1aa",
                  }}
                >
                  🥈
                </div>
                <div className="segment-card-title">
                  <h3>925 Sterling Silver</h3>
                  <span className="segment-group-tag">Silver</span>
                </div>
                <span className="segment-hallmark">
                  BIS 925 Sterling Artefact hallmark
                </span>
              </div>
              <div className="segment-card-body">
                <div className="plan-row">
                  <span className="plan-label">Transit Mode</span>
                  <span className="plan-value">
                    Insured Consolidated Freight
                  </span>
                </div>
                <div className="plan-row">
                  <span className="plan-label">Packaging</span>
                  <span className="plan-value">
                    Tamper-evident batch pouch with net-weight certificate
                  </span>
                </div>
                <div className="plan-row">
                  <span className="plan-label">Transit Insurance</span>
                  <span className="plan-value">
                    ₹1.5 Crore transit insurance per consignment
                  </span>
                </div>
                <div className="plan-row">
                  <span className="plan-label">Custody Level</span>
                  <span className="plan-value">
                    Hub custodian countersign + driver biometric
                  </span>
                </div>
                <div className="plan-row">
                  <span className="plan-label">Delivery SLA</span>
                  <span className="plan-value">
                    T+2 within city cluster • T+4 inter-city
                  </span>
                </div>
                <div className="plan-row">
                  <span className="plan-label">Handling Fee</span>
                  <span className="plan-value fee-text">
                    ₹70 – ₹140 per lot
                  </span>
                </div>
                <div className="plan-row">
                  <span className="plan-label">Typical Volume</span>
                  <span className="plan-value">80 – 250 kg</span>
                </div>
                <div className="plan-row">
                  <span className="plan-label">Key Corridors</span>
                  <span className="plan-value">
                    Coimbatore, Tirupur, Rajkot
                  </span>
                </div>
              </div>
              <div className="segment-card-footer">
                <span className="coverage-tag">
                  Coverage: South &amp; West India corridors
                </span>
                <button
                  className="btn btn-secondary btn-sm plan-apply-btn"
                  onClick={() => openAuth("operator")}
                >
                  Apply for This Segment Lane →
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── SECTION 3: CHOOSE YOUR BUSINESS SEGMENT ─────────────────────────────── */}
      <section className="segments-showcase-section" id="segments-section">
        <div className="jmi-container">
          <div className="section-header-center">
            <div className="section-label">
              11 DISTINCT OPERATOR APPOINTMENTS
            </div>
            <h2 className="section-title">
              Choose Your{" "}
              <span className="gold-gradient-text">Operator Segment</span>
            </h2>
            <p className="section-subtitle">
              Select exactly one of the 11 segments you understand and can
              develop. Your approved territory — one district, multiple
              districts, one state, or multiple states — is determined
              separately based on your capability, operational resources, and
              JMI approval. One operator is appointed per segment within an
              approved territory, per JMI&apos;s appointment policy.
            </p>

            <div className="segment-active-alert">
              <span>Selected Appointment:</span>
              <strong>
                {activeSegmentObj.operatorTitle} ({activeSegmentObj.key})
              </strong>
              <span>
                • Click any segment below to switch your single application
                focus
              </span>
            </div>
          </div>

          <div className="segments-grid">
            {JMI_SEGMENTS.map((seg) => {
              const isSelected = selectedSegment === seg.key;
              return (
                <div
                  key={seg.key}
                  className={`segment-card ${isSelected ? "selected-card" : ""}`}
                  onClick={() => handleSelectSegment(seg.key)}
                >
                  <div className="segment-card-image-box">
                    <img
                      src={seg.image}
                      alt={`${seg.label} jewellery trade`}
                      className="segment-card-image"
                      loading="lazy"
                    />
                    <div className="segment-image-overlay"></div>
                    <span className="segment-badge-tag">{seg.badge}</span>
                  </div>

                  <div className="segment-card-content">
                    <div className="segment-title-row">
                      <h3 className="segment-title">
                        {seg.operatorTitle || seg.label}
                      </h3>
                      <span className="segment-code-tag">
                        {seg.label} · ID: {seg.key}
                      </span>
                    </div>

                    <p className="segment-tagline">{seg.tagline}</p>

                    <div className="segment-meta-specs">
                      <div className="meta-spec-row">
                        <span className="meta-lbl">Standards:</span>
                        <span className="meta-val">{seg.specs}</span>
                      </div>
                      <div className="meta-spec-row">
                        <span className="meta-lbl">Target Buyers:</span>
                        <span className="meta-val">{seg.marketFocus}</span>
                      </div>
                    </div>

                    <button
                      type="button"
                      className="segment-select-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelectSegment(seg.key);
                      }}
                    >
                      {isSelected
                        ? `✓ Selected: ${seg.operatorTitle || seg.label} (1 of 1)`
                        : `Apply as ${seg.operatorTitle || seg.label} →`}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── SECTION 4: HOW THE JMI MODEL WORKS (5 STEPS) ────────────────────────── */}
      <section className="model-workflow-section" id="how-model-works">
        <div className="jmi-container">
          <div className="section-header-center">
            <div className="section-label">OPERATING BLUEPRINT</div>
            <h2 className="section-title">
              How the{" "}
              <span className="gold-gradient-text">
                JMI Partnership Model Works
              </span>
            </h2>
            <p className="section-subtitle">
              A structured, repeatable five-stage operating engine bridging
              regional ground presence with national digital supply.
            </p>
          </div>

          <div className="workflow-steps-track">
            <div className="wf-step-card">
              <span className="wf-step-num">STAGE 01</span>
              <span className="wf-step-icon">🤝</span>
              <h3 className="wf-step-title">Identify &amp; Develop</h3>
              <p className="wf-step-desc">
                The operator identifies, visits, and nurtures qualified
                wholesale jewellery buyers, retailers, and distributors within
                their approved geographic corridor.
              </p>
            </div>

            <div className="wf-step-card">
              <span className="wf-step-num">STAGE 02</span>
              <span className="wf-step-icon">📲</span>
              <h3 className="wf-step-title">Joint App Onboarding</h3>
              <p className="wf-step-desc">
                JMI and the operator jointly onboard verified buyers onto the
                JMI platform with GSTIN compliance checks and secure app
                credential provisioning.
              </p>
            </div>

            <div className="wf-step-card">
              <span className="wf-step-num">STAGE 03</span>
              <span className="wf-step-icon">🔎</span>
              <h3 className="wf-step-title">Pan-India Discovery</h3>
              <p className="wf-step-desc">
                Buyers discover hallmarked catalogues, live spot bullion prices,
                and structured wholesale inventories from participating sellers
                nationwide.
              </p>
            </div>

            <div className="wf-step-card">
              <span className="wf-step-num">STAGE 04</span>
              <span className="wf-step-icon">🛠️</span>
              <h3 className="wf-step-title">Ongoing Assistance</h3>
              <p className="wf-step-desc">
                The operator provides local buyer assistance, order
                clarification, inspection coordination, and agreed post-order
                relationship services.
              </p>
            </div>

            <div className="wf-step-card">
              <span className="wf-step-num">STAGE 05</span>
              <span className="wf-step-icon">⚡</span>
              <h3 className="wf-step-title">Commercial Settlement</h3>
              <p className="wf-step-desc">
                Transactions follow JMI’s institutional escrow, billing, and
                settlement protocols, generating agreed operator compensation
                per transaction.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── SECTION 5: WHO DOES WHAT? (RESPONSIBILITY MATRIX) ───────────────────── */}
      <section className="roles-matrix-section">
        <div className="jmi-container">
          <div className="section-header-center">
            <div className="section-label">
              CLEAR BOUNDARIES &amp; PARTNERSHIP ROLES
            </div>
            <h2 className="section-title">
              Who Does What?{" "}
              <span className="gold-gradient-text">
                JMI vs. Market Operator
              </span>
            </h2>
            <p className="section-subtitle">
              Clear demarcation of responsibilities ensures focus, high service
              standards, and operational excellence.
            </p>
          </div>

          <div className="roles-matrix-grid">
            <div className="role-scope-card jmi-side">
              <span className="role-card-badge">
                CENTRAL PLATFORM RESPONSIBILITY
              </span>
              <h3 className="role-scope-title">What JMI Provides</h3>
              <div className="role-item-list">
                <div className="role-item-row">
                  <span className="role-item-icon">💻</span>
                  <div className="role-item-text">
                    <strong>
                      Central Technology Platform &amp; Mobile Apps:
                    </strong>
                    <p>
                      Continuous development, cloud infrastructure, Android/iOS
                      apps, and server uptime.
                    </p>
                  </div>
                </div>
                <div className="role-item-row">
                  <span className="role-item-icon">🏭</span>
                  <div className="role-item-text">
                    <strong>Pan-India Participating Sellers:</strong>
                    <p>
                      Direct supply integrations with audited manufacturers in
                      Surat, Rajkot, Jaipur, Kolkata, Mumbai, and Thrissur.
                    </p>
                  </div>
                </div>
                <div className="role-item-row">
                  <span className="role-item-icon">🛡️</span>
                  <div className="role-item-text">
                    <strong>
                      Institutional Compliance &amp; Verification Rails:
                    </strong>
                    <p>
                      GST verification, BIS hallmarking audits, digital
                      contracts, and platform security standards.
                    </p>
                  </div>
                </div>
                <div className="role-item-row">
                  <span className="role-item-icon">💳</span>
                  <div className="role-item-text">
                    <strong>Escrow &amp; Commercial Processing:</strong>
                    <p>
                      Banking APIs, bullion spot lock engine, payment clearing,
                      and dispute mediation.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="role-scope-card operator-side">
              <span className="role-card-badge">
                OPERATOR GROUND LEADERSHIP
              </span>
              <h3 className="role-scope-title">
                What the Market Operator Handles
              </h3>
              <div className="role-item-list">
                <div className="role-item-row">
                  <span className="role-item-icon">📍</span>
                  <div className="role-item-text">
                    <strong>
                      Local Buyer Acquisition &amp; Relationship Building:
                    </strong>
                    <p>
                      Meeting local showroom owners, jeweller associations, and
                      establishing high-trust business rapport.
                    </p>
                  </div>
                </div>
                <div className="role-item-row">
                  <span className="role-item-icon">📲</span>
                  <div className="role-item-text">
                    <strong>Joint Buyer App Onboarding:</strong>
                    <p>
                      Guiding retail buyers through app download, KYC
                      submission, and catalog orientation.
                    </p>
                  </div>
                </div>
                <div className="role-item-row">
                  <span className="role-item-icon">🤝</span>
                  <div className="role-item-text">
                    <strong>Dedicated Customer Assistance:</strong>
                    <p>
                      Serving as the first point of contact for order guidance,
                      variant selections, and delivery coordination.
                    </p>
                  </div>
                </div>
                <div className="role-item-row">
                  <span className="role-item-icon">📈</span>
                  <div className="role-item-text">
                    <strong>Segment Trade Growth:</strong>
                    <p>
                      Driving repeat purchase frequency, local market insights,
                      and expanding corridor density.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="joint-responsibility-banner">
            <span className="joint-badge">JOINT COMMITMENT</span>
            <p className="joint-desc">
              <strong>Buyer Onboarding is a Coordinated Joint Effort:</strong>{" "}
              The operator introduces and vouches for the buyer in the field,
              while JMI verifies business credentials, provisions secure digital
              keys, and provides technical app support.
            </p>
          </div>

          {/* JMI Rule Matrix: Segment Operator Partnership Model */}
          <div className="model-rules-card" id="model-rules">
            <h3 className="model-rules-title">
              How the Model Works — JMI Rules
            </h3>
            <p className="model-rules-sub">
              One operator is responsible for one business segment, while
              geographic coverage can range from one district to multiple states
              depending on capability. JMI approves the segment and the
              territory separately.
            </p>
            <div className="model-rules-table-wrap">
              <table className="model-rules-table">
                <thead>
                  <tr>
                    <th>Dimension</th>
                    <th>JMI Rule</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td data-label="Dimension">Segment</td>
                    <td data-label="JMI Rule">
                      One operator per segment, according to JMI&apos;s
                      appointment policy.
                    </td>
                  </tr>
                  <tr>
                    <td data-label="Dimension">Geography</td>
                    <td data-label="JMI Rule">
                      One district, multiple districts, one state, or multiple
                      states — approved per operator capability.
                    </td>
                  </tr>
                  <tr>
                    <td data-label="Dimension">Buyer acquisition</td>
                    <td data-label="JMI Rule">
                      Operator and JMI work jointly to develop buyers for the
                      assigned segment.
                    </td>
                  </tr>
                  <tr>
                    <td data-label="Dimension">Buyer onboarding</td>
                    <td data-label="JMI Rule">
                      Jointly completed through the JMI app with verification
                      and app support.
                    </td>
                  </tr>
                  <tr>
                    <td data-label="Dimension">Seller network</td>
                    <td data-label="JMI Rule">
                      Participating sellers from across India, accessible
                      through the platform subject to availability and
                      applicable policies.
                    </td>
                  </tr>
                  <tr>
                    <td data-label="Dimension">Buyer services</td>
                    <td data-label="JMI Rule">
                      Assigned operator handles agreed assistance and
                      relationship management for their segment.
                    </td>
                  </tr>
                  <tr>
                    <td data-label="Dimension">Technology</td>
                    <td data-label="JMI Rule">
                      JMI provides the platform and app support.
                    </td>
                  </tr>
                  <tr>
                    <td data-label="Dimension">Earnings</td>
                    <td data-label="JMI Rule">
                      Defined by the signed operator commercial agreement; no
                      guaranteed income.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="model-rules-note">
              <strong>Important distinction:</strong> one segment per operator
              does not automatically mean only one operator can exist
              nationally. A single exclusive operator for each segment across
              all of India is a different and much stronger exclusivity rule.
              Otherwise, JMI appoints one operator per segment within each
              approved territory.
            </p>
          </div>
        </div>
      </section>

      {/* ── SECTION 6: TERRITORY & OPERATING CAPACITY ──────────────────────────── */}
      <section className="territory-scope-section">
        <div className="jmi-container">
          <div className="section-header-center">
            <div className="section-label">GEOGRAPHIC COVERAGE TIERS</div>
            <h2 className="section-title">
              Territory &amp;{" "}
              <span className="gold-gradient-text">Operating Capacity</span>
            </h2>
            <p className="section-subtitle">
              Approved coverage depends on demonstrated buyer-acquisition
              capability, service capacity, and JMI review—not merely
              preference. One operator is appointed per segment within an
              approved territory.
            </p>
          </div>

          <div className="territory-grid">
            <div className="territory-card">
              <span className="territory-tier-tag">TIER 1</span>
              <h3 className="territory-title">Single District</h3>
              <p className="territory-desc">
                Ideal for established local jewellers or distributors focusing
                deeply on a single district's retail jewelry bazaars.
              </p>
              <div className="territory-criteria-box">
                <strong>Expected Target:</strong> 30 – 75 Retail Showroom buyers
                onboarded.
              </div>
            </div>

            <div className="territory-card">
              <span className="territory-tier-tag">TIER 2</span>
              <h3 className="territory-title">Multi-District Cluster</h3>
              <p className="territory-desc">
                For regional operators with existing distribution van networks
                or field representatives across 2 to 5 adjacent districts.
              </p>
              <div className="territory-criteria-box">
                <strong>Expected Target:</strong> 100 – 250 Retail Showroom
                buyers onboarded.
              </div>
            </div>

            <div className="territory-card">
              <span className="territory-tier-tag">TIER 3</span>
              <h3 className="territory-title">Single State Lead</h3>
              <p className="territory-desc">
                Awarded to experienced wholesale entities capable of
                orchestrating statewide buyer acquisition and customer support.
              </p>
              <div className="territory-criteria-box">
                <strong>Expected Target:</strong> 300 – 750 Verified Retail
                Showrooms.
              </div>
            </div>

            <div className="territory-card">
              <span className="territory-tier-tag">TIER 4</span>
              <h3 className="territory-title">Multi-State Corridor</h3>
              <p className="territory-desc">
                Strategic interstate enterprise franchise for large bullion
                houses, vault operators, or multi-branch logistics groups.
              </p>
              <div className="territory-criteria-box">
                <strong>Expected Target:</strong> 1,000+ Verified Buyers across
                economic zones.
              </div>
            </div>
          </div>

          <div className="governance-note-box">
            <strong>JMI Operating Standard:</strong> One operator is appointed
            per business segment within an approved territory. Territories are
            granted on merit and performance; JMI does not grant speculative or
            dormant territorial rights.
          </div>
        </div>
      </section>

      {/* ── SECTION 7: COMMERCIAL OPPORTUNITY ──────────────────────────────────── */}
      <section className="commercial-terms-section">
        <div className="jmi-container">
          <div className="section-header-center">
            <div className="section-label">COMMERCIAL TRANSPARENCY</div>
            <h2 className="section-title">
              The Commercial{" "}
              <span className="gold-gradient-text">Opportunity Structure</span>
            </h2>
            <p className="section-subtitle">
              A performance-linked partnership designed to reward proactive
              market building, high buyer retention, and superior service.
            </p>
          </div>

          <div className="commercial-pillars-grid">
            <div className="commercial-pillar-card">
              <div className="pillar-icon-box">📊</div>
              <h3 className="pillar-title">Transaction-Based Compensation</h3>
              <p className="pillar-desc">
                Operators receive an agreed share of platform fees on every
                completed wholesale trade executed by buyers in their assigned
                segment and approved territory.
              </p>
            </div>

            <div className="commercial-pillar-card">
              <div className="pillar-icon-box">🔄</div>
              <h3 className="pillar-title">Recurring Volume Compounding</h3>
              <p className="pillar-desc">
                Unlike one-off commission models, onboarded buyers trade
                repeatedly throughout festive, wedding, and bullion
                replenishment cycles, building continuous monthly earnings.
              </p>
            </div>

            <div className="commercial-pillar-card">
              <div className="pillar-icon-box">⏱️</div>
              <h3 className="pillar-title">Transparent Settlement Cycles</h3>
              <p className="pillar-desc">
                All transaction proceeds and operator distributions are
                calculated in real time and disbursed per signed commercial
                schedules via direct banking settlement.
              </p>
            </div>
          </div>

          <div className="commercial-disclaimer-card">
            <span className="disclaimer-icon">⚖️</span>
            <div className="disclaimer-copy">
              <strong>Responsible Business Practice Notice:</strong> JMI does
              not publish speculative earning estimates or promise guaranteed
              incomes. Financial returns depend entirely on actual business
              execution, buyer acquisition volume, trade frequency, and
              operational diligence in your chosen territory.
            </div>
          </div>
        </div>
      </section>

      {/* Operator Application Form Section */}
      <section className="operator-application-section" id="operator-application-form">
        <div className="jmi-container">
          <div className="application-form-wrapper">
            <div className="form-intro-col">
              <div className="form-intro-sticky">
                <div className="section-label">FRANCHISE APPLICATION</div>
                <h2 className="section-title">
                  Apply for Your <span className="gold-gradient-text">Regional Operator Hub</span>
                </h2>
                <p className="form-intro-text">
                  Complete the application form to secure your exclusive city franchise. Our franchise 
                  team will review your application and contact you within 48 hours to schedule a 
                  detailed franchise discussion.
                </p>
                
                <div className="form-benefits-list">
                  <div className="form-benefit-item">
                    <span className="benefit-icon">⚡</span>
                    <div className="benefit-content">
                      <h4>Fast-Track Review</h4>
                      <p>48-hour application review with priority processing</p>
                    </div>
                  </div>
                  <div className="form-benefit-item">
                    <span className="benefit-icon">🎯</span>
                    <div className="benefit-content">
                      <h4>Exclusive Territory</h4>
                      <p>Lock your city cluster before competitors apply</p>
                    </div>
                  </div>
                  <div className="form-benefit-item">
                    <span className="benefit-icon">📊</span>
                    <div className="benefit-content">
                      <h4>Detailed Financial Model</h4>
                      <p>Receive custom hub economics for your city</p>
                    </div>
                  </div>
                  <div className="form-benefit-item">
                    <span className="benefit-icon">🤝</span>
                    <div className="benefit-content">
                      <h4>Setup Support</h4>
                      <p>Complete training & infrastructure guidance</p>
                    </div>
                  </div>
                </div>

                <div className="form-trust-badge">
                  <span className="trust-icon">🛡️</span>
                  <div className="trust-text">
                    <strong>100% Secure Application</strong>
                    <p>Your information is encrypted and never shared with third parties</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="form-card-col">
              <OnboardingModal 
                isOpen={true} 
                initialRole="operator"
                onClose={() => {}} 
                isModal={false}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Operator CTA Banner */}
      <section className="operator-cta-banner-section">
        <div className="jmi-container">
          <div className="operator-banner-card">
            <div className="banner-gold-badge">
              LIMITED EXCLUSIVE CLUSTERS AVAILABLE
            </div>
            <h2>Lock Your City Franchise Before Competitors Do</h2>
            <p>
              Each jewellery corridor is awarded to only{" "}
              <strong>one exclusive JMI Regional Operator</strong>. Invest ~₹10
              Lakhs in high-grade vault infrastructure and start earning up to
              ₹50 Lakhs+ annually.
            </p>
            <div className="banner-actions">
              <button
                className="btn btn-primary btn-lg"
                onClick={() => openAuth("operator")}
              >
                Apply for Operator Hub Franchise →
              </button>
              <a
                href="tel:+919053999090"
                className="btn btn-outline-gold btn-lg"
              >
                Schedule Franchise Call
              </a>
            </div>
            <div className="banner-trust-sub">
              Authorized B2B Franchise Licensing by{" "}
              <strong>7RAYS GLOBAL PRIVATE LIMITED</strong> (CIN:
              U47733GJ2026PTC171774), Surat, Gujarat, 395010 - India.
            </div>
          </div>
        </div>
      </section>

      <Footer onOpenAuthModal={openAuth} />
      <OnboardingModal
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        initialRole={authModalRole}
      />
    </div>
  );
};

export default OperatorPage;
